import { useState } from "react";
import { api } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { X, Variable } from "lucide-react";

const variables = [
  { name: "Name", value: "{{name}}" },
  { name: "Company", value: "{{company}}" },
  { name: "Contact Person", value: "{{contact_person}}" },
  { name: "Vessel Type", value: "{{vessel_type}}" },
  { name: "Email", value: "{{email}}" },
  { name: "Phone", value: "{{phone}}" }
];

export default function CreateTemplateModal({ onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [useSamplePreview, setUseSamplePreview] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    subject: "",
    content: "",
    is_default: false
  });

  const sampleData = {
    name: "MV Ocean Star",
    vessel_name: "MV Ocean Star",
    company: "Maritime Corp Ltd",
    contact_person: "Captain John Smith",
    email: "ocean.star@maritime.com",
    port: "Rotterdam",
    vessel_type: "Container Ship",
    phone: "+1 234 567 8900"
  };

  const applyVariables = (text) => {
    if (!useSamplePreview || !text) return text;
    let result = text;
    Object.entries(sampleData).forEach(([key, value]) => {
      result = result.replaceAll(`{{${key}}}`, value);
    });
    return result;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById('content');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const before = text.substring(0, start);
      const after = text.substring(end);
      const newText = before + variable + after;
      
      setFormData(prev => ({ ...prev, content: newText }));
      
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(start + variable.length, start + variable.length);
      }, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.subject || !formData.content) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);

    try {
      await api.post("/templates", formData);
      toast.success("Template created successfully");
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create template");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center modal-overlay p-4">
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto animate-fadeIn">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Create Email Template
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Template Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Template Information
            </h3>
            
            <div className="space-y-2">
              <Label htmlFor="name">Template Name *</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Welcome Email, Port Update, etc."
                data-testid="template-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject Line *</Label>
              <Input
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                placeholder="Important Updates for {{name}}"
                data-testid="template-subject-input"
              />
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="is_default"
                name="is_default"
                checked={formData.is_default}
                onCheckedChange={(checked) => 
                  setFormData(prev => ({ ...prev, is_default: checked }))
                }
              />
              <Label htmlFor="is_default" className="cursor-pointer">
                Set as default template
              </Label>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                Email Content
              </h3>
              <span className="text-xs text-muted-foreground">
                Use variables to personalize your emails
              </span>
            </div>

            {/* Variable Buttons */}
            <div className="p-4 bg-secondary/30 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Variable className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Insert Variable:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {variables.map((variable) => (
                  <Button
                    key={variable.value}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => insertVariable(variable.value)}
                    className="text-xs"
                  >
                    {variable.name}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content *</Label>
              <Textarea
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                required
                rows={12}
                placeholder="Dear {{contact_person}},&#10;&#10;We hope this message finds you well. We wanted to reach out regarding {{name}}...&#10;&#10;Best regards,&#10;Your Maritime Team"
                className="font-mono text-sm"
                data-testid="template-content-input"
              />
            </div>
          </div>

          {/* Preview */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Preview
            </h3>
            <div className="flex items-center gap-2">
              <Checkbox
                id="sample-preview"
                checked={useSamplePreview}
                onCheckedChange={(checked) => setUseSamplePreview(Boolean(checked))}
              />
              <Label htmlFor="sample-preview" className="cursor-pointer">
                Preview with sample data
              </Label>
            </div>
            
            <div className="border border-border rounded-lg p-4 bg-secondary/20">
              <div className="mb-3 pb-3 border-b border-border">
                <p className="text-xs text-muted-foreground mb-1">Subject:</p>
                <p className="font-medium">{applyVariables(formData.subject) || "Subject will appear here..."}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Content:</p>
                <pre className="whitespace-pre-wrap font-sans text-sm">
                  {applyVariables(formData.content) || "Email content will appear here..."}
                </pre>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="btn-primary" 
              disabled={loading}
              data-testid="create-template-submit"
            >
              {loading ? "Creating..." : "Create Template"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
